<template>
  <div class="container inner mx-auto px-4 py-8">
    <div class="lg:flex lg:space-x-8">
      <aside class="lg:w-1/4 mb-8 lg:mb-0">
        <FilterPanel
          :availableLeagues="availableLeagues"
          @updateFilter="handleFilterUpdate"
        />
      </aside>
      <main class="lg:w-3/4 ">
        <div v-if="loading" class="flex justify-center items-center h-64">
          <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <div v-else-if="error" class="text-red-500 text-center">
          {{ error }}
        </div>
        <TransitionGroup
          v-else
          name="match-list"
          tag="div"
          class="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          <MatchCard
            v-for="match in sortedResults"
            :key="match.id"
            :match="match"
          />
        </TransitionGroup>
        <div v-if="!loading && !error && sortedResults.length === 0" class="text-center text-gray-400 mt-8">
          No results for selected filters.
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import FilterPanel from './FilterPanel.vue';
import MatchCard from './MatchCard.vue';


const results = ref([]);
const loading = ref(false);
const error = ref('');
const filter = ref({ sport: null, league: null });
const availableLeagues = computed(() => {
  const leaguesMap = new Map();
  results.value.forEach(match => {
    if (!match.league || !match.sport) return;
    const group = mapSportToGroup(match.sport);
    if (!leaguesMap.has(match.league)) {
      leaguesMap.set(match.league, {
        key: match.league,
        title: beautifyLeagueTitle(match.league),
        group
      });
    }
  });
  return Array.from(leaguesMap.values());
});
const filteredResults = computed(() => {
  if (filter.value.league) {
    return results.value.filter(match => match.league === filter.value.league);
  }
  if (filter.value.sport) {
    const leaguesForSport = availableLeagues.value
      .filter(league => league.group === filter.value.sport)
      .map(league => league.key);
    return results.value.filter(match => leaguesForSport.includes(match.league));
  }
  return results.value;
});

function mapSportToGroup(sportKey) {
  if (!sportKey) return 'other';
  if (sportKey.startsWith('baseball_')) return 'baseball';
    if (sportKey.startsWith('cricket_')) return 'cricket';
  if (sportKey.startsWith('soccer_')) return 'soccer';
  if (sportKey.startsWith('basketball_')) return 'basketball';
  if (sportKey.startsWith('icehockey_')) return 'icehockey';
  if (sportKey.startsWith('rugbyleague_')) return 'rugbyleague';
  if (sportKey.startsWith('rugbyunion_')) return 'rugbyunion';
  if (sportKey.startsWith('esports_')) return 'esports';
  if (sportKey.startsWith('americanfootball')) return 'americanfootball';
    if (sportKey.startsWith('aussierules_afl')) return 'americanfootball';

  // Если уже чистое значение (например, 'cricket'), просто возвращаем его:
  return sportKey;
}
function beautifyLeagueTitle(leagueKey) {
  let text = leagueKey;
  const knownPrefixes = [
    'soccer_',
    'basketball_',
    'icehockey_',
    'rugbyleague_',
    'rugbyunion_',
    'esports_'
  ];
  for (const prefix of knownPrefixes) {
    if (text.startsWith(prefix)) {
      text = text.replace(prefix, '');
      break;
    }
  }
  text = text.replaceAll('_', ' ');
  text = text
    .split(' ')
    .map(word => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  if (text.toLowerCase() === 'epl') {
    text = 'EPL';
  }
  return text;
}
const sortedResults = computed(() => {
  return [...filteredResults.value].sort(
    (a, b) =>  new Date(a.date) - new Date(b.date)
  );
});

async function fetchResults() {
  loading.value = true;
  error.value = '';
  try {
    const response = await fetch('/api/analysis');
    if (!response.ok) {
      throw new Error('Error fetching results from server');
    }
    const data = await response.json();

results.value = Array.isArray(data)
  ? data.filter(item => item.coefficient <= 2.9)
  : [];
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

function handleFilterUpdate(newFilter) {
  filter.value = newFilter;
}

onMounted(() => {
  fetchResults();
});
</script>

<style scoped>
.match-list-enter-active,
.match-list-leave-active {
  transition: all 0.5s ease;
}

.match-list-enter-from,
.match-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
